import { inject, Injectable } from '@angular/core';
import { ContentfulConfigService } from '@scalefast/config-angular';
import type { ContentfulClientApi } from 'contentful';
import { NoChainModifier } from 'src/app/core/interfaces/Contentful/ContentfulChainModifier';
import { getContentfulConfig } from '../helpers/contentful.helper';
import { MECACHIS_DEPENDENCY_SERVICE } from '@scalefast/ecommerce-core';

@Injectable({
  providedIn: 'root',
})
export class ContentfulClientService {
  #contentfulConfig;
  #client?: ContentfulClientApi<NoChainModifier>;
  #contentfulConfigService = inject(ContentfulConfigService);
  mecachisDependencyService = inject(MECACHIS_DEPENDENCY_SERVICE);

  constructor() {
    this.#contentfulConfig = getContentfulConfig(this.#contentfulConfigService);
  }

  async getClient(): Promise<ContentfulClientApi<NoChainModifier>> {
    if (!this.#client) {
      // With dynamic import we can avoid loading Contentful in the browser bundle.
      // This way reduce the bundle size and improve the performance.
      // The Contentful library will be loaded only when the client is requested.
      const contentful = await import('contentful');
      // Ensure compatibility with different module systems by checking if createClient is under contentful.default or directly under contentful.
      // .default is used in build/start, without default in dev-server (serve and browser-serve)
      const createClient = contentful.default?.createClient ?? contentful.createClient;
      this.#client = createClient({
        ...this.#contentfulConfig,
        responseLogger: this.createRevalidableContentfulDependencyInterceptor(this.#contentfulConfig.accessToken),
      });
    }

    return this.#client;
  }

  private createRevalidableContentfulDependencyInterceptor(apiKey: string) {
    return (response: any) => {
      const url = new URL(response.config.url);
      const params = response.config.params;
      const etag = response.headers.get('etag');
      Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));
      this.mecachisDependencyService.addRevalidableContentfulDependency(url.toString(), {
        etag,
        apiKey,
        timeToRevalidate: 60 * 60,
      });
    };
  }

  getLocale(): string {
    return this.#contentfulConfig.locale;
  }
}
